import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  CreditCard,
  DollarSign,
  ExternalLink,
  FileText,
  HeartPulse,
  Link2,
  NotebookPen,
  Plus,
  Search,
  Trash2,
  Users,
} from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { FormPortalHeader } from "./FormPortalHeader";
import {
  calculateBalance,
  checklistStatusOptions,
  clientStatusOptions,
  clientTypeOptions,
  createClientRecord,
  currency,
  documentTypeOptions,
  followUpPriorityOptions,
  followUpStatusOptions,
  formatDateTime,
  formatDisplayDate,
  getClientHealth,
  healthOptions,
  isDateOverdue,
  loadClients,
  noteCategoryOptions,
  paymentStatusOptions,
  refreshClients,
  saveClients,
  serviceOptions,
  sortFollowUps,
  sortNotesNewestFirst,
  upsertClient,
  type ChecklistStatus,
  type ClientDocument,
  type ClientFollowUp,
  type ClientHealth,
  type ClientNote,
  type ClientQuickLink,
  type ClientStatus,
  type ClientType,
  type DocumentType,
  type FollowUpPriority,
  type FollowUpStatus,
  type KtdClientRecord,
  type NoteCategory,
  type PaymentStatus,
} from "./clientManagementData";

const blankClientForm = {
  clientName: "",
  email: "",
  phone: "",
  organizationName: "",
  clientType: "Individual" as ClientType,
  eventName: "",
  eventDate: "",
  selectedServices: [] as string[],
  selectedPackage: "",
  projectStatus: "New Lead" as ClientStatus,
  notes: "",
};

const quickLinkLabels = [
  "Proposal",
  "Service Agreement",
  "Invoice / Payment Link",
  "Memory Media Vault",
  "Client Forms",
  "Archive Hub",
  "Final Delivery",
  "Other Custom Link",
];

export function FormsClientDirectoryPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<KtdClientRecord[]>([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | ClientStatus>("All");
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(blankClientForm);

  useEffect(() => {
    document.title = "Client Management | Business Center OS";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", "Shared Business Center OS client management and project tracking dashboard.");
    let cancelled = false;
    refreshClients()
      .then((nextClients) => {
        if (!cancelled) setClients(nextClients);
      })
      .catch(() => {
        if (!cancelled) setClients(loadClients());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredClients = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return clients.filter((client) => {
      const matchesStatus = statusFilter === "All" || client.projectStatus === statusFilter;
      const matchesQuery =
        !normalizedQuery ||
        [client.clientName, client.email, client.eventName, client.organizationName, client.selectedPackage, client.selectedServices.join(" ")]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      return matchesStatus && matchesQuery;
    });
  }, [clients, query, statusFilter]);

  const dashboardSummary = useMemo(() => {
    const activeProjects = clients.filter((client) => client.projectStatus !== "Completed").length;
    const awaitingClientResponse = clients.filter((client) =>
      ["Awaiting Approval", "Awaiting Client Content", "On Hold", "Client Review"].includes(client.projectStatus),
    ).length;
    const paymentsDue = clients.filter((client) =>
      ["Payment Link Sent", "Deposit Due", "Balance Due"].includes(client.paymentSummary.paymentStatus),
    ).length;
    const followUpsDue = clients.filter((client) =>
      client.followUps.some((followUp) => followUp.status === "Pending" && isDateOverdue(followUp.followUpDate)),
    ).length;
    const completed = clients.filter((client) => client.projectStatus === "Completed").length;
    return [
      { label: "Total Clients", value: clients.length, icon: Users },
      { label: "Active Projects", value: activeProjects, icon: Activity },
      { label: "Awaiting Client Response", value: awaitingClientResponse, icon: AlertTriangle },
      { label: "Payments Due", value: paymentsDue, icon: DollarSign },
      { label: "Follow-Ups Due", value: followUpsDue, icon: ClipboardList },
      { label: "Projects Completed", value: completed, icon: CheckCircle2 },
    ];
  }, [clients]);

  function updateForm<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function toggleService(service: string) {
    setForm((current) => ({
      ...current,
      selectedServices: current.selectedServices.includes(service)
        ? current.selectedServices.filter((item) => item !== service)
        : [...current.selectedServices, service],
    }));
  }

  function addClient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const client = createClientRecord(form);
    const nextClients = upsertClient(clients, client);
    setClients(nextClients);
    setForm(blankClientForm);
    setShowAddForm(false);
    navigate(`/admin/forms-client/${client.id}`);
  }

  return (
    <main className="command-center-shell min-h-screen text-[#fff9ef]">
      <FormPortalHeader
        brandHref="/admin/truevance-business-center"
        brandName="Business Center OS"
        brandSubtitle="Shared client operations"
        brandLogoAlt="Business Center OS"
        adminDirectoryHref="/admin/truevance-business-center"
        adminDirectoryLabel="Business Center Home"
        showPublicBackLink={false}
      />
      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,106,43,.18),transparent_34%),linear-gradient(120deg,rgba(5,5,5,.96),rgba(22,18,10,.9))]" />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-5">
          <Link to="/admin/truevance-business-center" className="focus-ring inline-flex w-fit items-center gap-2 rounded-lg text-sm font-bold text-[#FF6A2B]">
            <ArrowLeft className="size-4" aria-hidden="true" />
            Business Center Home
          </Link>
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6A2B]">Internal Operations</p>
          <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">Client Management</h1>
          <p className="max-w-3xl text-base font-semibold leading-8 text-[#fff9ef]/76">
            Manage client records, project timelines, documents, notes, payments, and follow-up activities across
            Business Center OS workspaces.
          </p>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
            {dashboardSummary.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.label} className="command-center-card rounded-lg p-4" title={`${item.label} summary`}>
                  <Icon className="size-5 text-[#FF6A2B]" aria-hidden="true" />
                  <p className="mt-4 font-display text-3xl font-bold text-[#fff9ef]">{item.value}</p>
                  <p className="mt-1 text-xs font-black uppercase tracking-[0.12em] text-[#fff9ef]/66">{item.label}</p>
                  <p className="mt-3 text-sm font-semibold leading-6 text-[#fff9ef]/72">
                    At-a-glance client operations status for the current filtered set.
                  </p>
                </article>
              );
            })}
          </div>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <div className="flex flex-col gap-4 border-b border-[#D94A16]/16 pb-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Master Client List</p>
                <h2 className="mt-1 font-display text-3xl font-bold text-[#fff9ef]">Client Directory</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowAddForm((current) => !current)}
                className="admin-primary-button focus-ring"
                title="Open the new client form"
              >
                <Plus className="size-4" aria-hidden="true" />
                Add New Client
              </button>
            </div>

            {showAddForm ? (
              <form onSubmit={addClient} className="mt-5 grid gap-4 rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4 md:grid-cols-2">
                <TextInput label="Client Name" required value={form.clientName} onChange={(value) => updateForm("clientName", value)} />
                <TextInput label="Email" type="email" value={form.email} onChange={(value) => updateForm("email", value)} />
                <TextInput label="Phone" type="tel" value={form.phone} onChange={(value) => updateForm("phone", value)} />
                <TextInput
                  label="Organization / Family / Business Name"
                  value={form.organizationName}
                  onChange={(value) => updateForm("organizationName", value)}
                />
                <SelectInput
                  label="Client Type"
                  value={form.clientType}
                  options={clientTypeOptions}
                  onChange={(value) => updateForm("clientType", value as ClientType)}
                />
                <TextInput label="Event / Project Name" value={form.eventName} onChange={(value) => updateForm("eventName", value)} />
                <TextInput label="Event Date" type="date" value={form.eventDate} onChange={(value) => updateForm("eventDate", value)} />
                <TextInput label="Selected Package" value={form.selectedPackage} onChange={(value) => updateForm("selectedPackage", value)} />
                <SelectInput
                  label="Project Status"
                  value={form.projectStatus}
                  options={clientStatusOptions}
                  onChange={(value) => updateForm("projectStatus", value as ClientStatus)}
                />
                <div className="grid gap-2 md:col-span-2">
                  <p className="text-sm font-bold text-[#332a1f]">Selected Service(s)</p>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {serviceOptions.map((service) => (
                      <label key={service} className="flex items-center gap-2 rounded-lg border border-[#D94A16]/16 bg-white p-3 text-sm font-bold text-[#332a1f]">
                        <input
                          type="checkbox"
                          checked={form.selectedServices.includes(service)}
                          onChange={() => toggleService(service)}
                          className="size-4 accent-[#D94A16]"
                        />
                        {service}
                      </label>
                    ))}
                  </div>
                </div>
                <TextAreaInput className="md:col-span-2" label="Notes" value={form.notes} onChange={(value) => updateForm("notes", value)} />
                <div className="flex justify-end md:col-span-2">
                  <button type="submit" className="focus-ring min-h-11 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B]">
                    Save Client
                  </button>
                </div>
              </form>
            ) : null}

            <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_260px]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#FF6A2B]" aria-hidden="true" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="command-center-field focus-ring min-h-12 w-full rounded-lg pl-11 pr-4 text-sm font-semibold"
                  placeholder="Search by client, event, email, service, or package"
                />
              </label>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as "All" | ClientStatus)}
                className="command-center-field focus-ring min-h-12 rounded-lg px-4 text-sm font-bold"
              >
                <option value="All">All statuses</option>
                {clientStatusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-5 grid gap-4">
              {filteredClients.length ? (
                filteredClients.map((client) => <ClientDirectoryCard key={client.id} client={client} />)
              ) : (
                <div className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-6 text-center">
                  <p className="font-display text-2xl font-bold text-[#050505]">No client records yet</p>
                  <p className="mt-2 text-sm font-semibold text-[#5c5246]">
                    Your first client record will appear here after a lead is converted or a client is added manually.
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

function ClientDirectoryCard({ client }: { client: KtdClientRecord }) {
  const health = getClientHealth(client);
  return (
    <article className="grid gap-4 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B] hover:shadow-[0_18px_38px_rgba(45,32,8,.12)] lg:grid-cols-[1.1fr_.8fr_.7fr_.6fr_auto] lg:items-center">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-display text-2xl font-bold text-[#050505]">{client.clientName}</h3>
          <HealthBadge health={health} />
        </div>
        <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">{client.eventName || "No project name set"}</p>
        <p className="mt-2 text-sm font-semibold leading-6 text-[#5c5246]">
          Open the profile to manage notes, quick links, documents, follow-ups, and payment tracking.
        </p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{formatDisplayDate(client.eventDate)}</p>
        <p className="mt-1 text-[#5c5246]">{client.selectedPackage || "Package not set"}</p>
      </div>
      <div className="text-sm font-bold text-[#332a1f]">
        <p>{client.projectStatus}</p>
        <p className="mt-1 text-[#5c5246]">{client.selectedServices.join(", ") || "Services not set"}</p>
      </div>
      <div className="text-sm font-bold text-[#5c5246]">
        <p>Last updated</p>
        <p className="mt-1 text-[#332a1f]">{formatDateTime(client.updatedAt)}</p>
      </div>
      <Link
        to={`/admin/forms-client/${client.id}`}
        className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B]"
        title={`Open the profile for ${client.clientName}`}
      >
        View Client
        <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

export function FormsClientProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clients, setClients] = useState<KtdClientRecord[]>([]);
  const [client, setClient] = useState<KtdClientRecord | null>(null);
  const [info, setInfo] = useState<KtdClientRecord | null>(null);
  const [quickLink, setQuickLink] = useState({ label: "Proposal", url: "", notes: "" });
  const [note, setNote] = useState({
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().slice(0, 5),
    category: "Internal Note" as NoteCategory,
    text: "",
    followUpDate: "",
  });
  const [editingNoteId, setEditingNoteId] = useState("");
  const [documentForm, setDocumentForm] = useState({ type: "Proposal" as DocumentType, label: "", url: "", notes: "" });
  const [followUpForm, setFollowUpForm] = useState({
    followUpDate: "",
    reason: "",
    priority: "Medium" as FollowUpPriority,
    status: "Pending" as FollowUpStatus,
    notes: "",
  });

  useEffect(() => {
    document.title = "Client Profile | Business Center OS";
    const loaded = loadClients();
    const found = loaded.find((item) => item.id === id) || null;
    setClients(loaded);
    setClient(found);
    setInfo(found);
  }, [id]);

  if (!id) return <Navigate to="/admin/forms-client-directory" replace />;
  if (!client || !info) {
    return (
      <main className="min-h-screen bg-[#fff9ef] text-[#16120a]">
        <FormPortalHeader
          brandHref="/admin/truevance-business-center"
          brandName="Business Center OS"
          brandSubtitle="Shared client operations"
          brandLogoAlt="Business Center OS"
          adminDirectoryHref="/admin/truevance-business-center"
          adminDirectoryLabel="Business Center Home"
          showPublicBackLink={false}
        />
        <section className="px-4 py-12 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl rounded-lg border border-[#D94A16]/22 bg-white p-6 text-center">
            <p className="font-display text-3xl font-bold text-[#050505]">Client not found</p>
            <Link to="/admin/forms-client-directory" className="focus-ring mt-5 inline-flex rounded-lg bg-[#050505] px-5 py-3 text-sm font-extrabold text-[#FF6A2B]">
              Back to Client Directory
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const health = getClientHealth(client);
  const balance = calculateBalance(client.paymentSummary);

  function persist(nextClient: KtdClientRecord) {
    const nextClients = upsertClient(clients, nextClient);
    const updated = nextClients.find((item) => item.id === nextClient.id) || nextClient;
    setClients(nextClients);
    setClient(updated);
    setInfo(updated);
  }

  function saveClientInfo(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!info) return;
    persist(info);
  }

  function addQuickLink(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!client) return;
    if (!quickLink.url.trim()) return;
    const nextLink: ClientQuickLink = { id: crypto.randomUUID(), ...quickLink };
    persist({ ...client, quickLinks: [nextLink, ...client.quickLinks] });
    setQuickLink({ label: "Proposal", url: "", notes: "" });
  }

  function addOrUpdateNote(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!client) return;
    if (!note.text.trim()) return;
    if (editingNoteId) {
      persist({
        ...client,
        timelineNotes: client.timelineNotes.map((item) => (item.id === editingNoteId ? { ...item, ...note } : item)),
      });
      setEditingNoteId("");
    } else {
      const nextNote: ClientNote = { id: crypto.randomUUID(), ...note };
      persist({ ...client, timelineNotes: [nextNote, ...client.timelineNotes] });
    }
    setNote({
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().slice(0, 5),
      category: "Internal Note",
      text: "",
      followUpDate: "",
    });
  }

  function editNote(noteToEdit: ClientNote) {
    setEditingNoteId(noteToEdit.id);
    setNote({
      date: noteToEdit.date,
      time: noteToEdit.time,
      category: noteToEdit.category,
      text: noteToEdit.text,
      followUpDate: noteToEdit.followUpDate,
    });
  }

  function deleteNote(noteId: string) {
    if (!client) return;
    persist({ ...client, timelineNotes: client.timelineNotes.filter((item) => item.id !== noteId) });
  }

  function addDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!client) return;
    if (!documentForm.url.trim()) return;
    const documentRecord: ClientDocument = { id: crypto.randomUUID(), ...documentForm };
    persist({ ...client, documents: [documentRecord, ...client.documents] });
    setDocumentForm({ type: "Proposal", label: "", url: "", notes: "" });
  }

  function addFollowUp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!client) return;
    if (!followUpForm.followUpDate || !followUpForm.reason.trim()) return;
    const followUp: ClientFollowUp = { id: crypto.randomUUID(), ...followUpForm };
    persist({ ...client, followUps: [followUp, ...client.followUps] });
    setFollowUpForm({ followUpDate: "", reason: "", priority: "Medium", status: "Pending", notes: "" });
  }

  function updateChecklistItem(itemId: string, updates: Partial<{ status: ChecklistStatus; completionDate: string; notes: string }>) {
    if (!client) return;
    persist({
      ...client,
      checklist: client.checklist.map((item) => (item.id === itemId ? { ...item, ...updates } : item)),
    });
  }

  function updateFollowUp(itemId: string, updates: Partial<ClientFollowUp>) {
    if (!client) return;
    persist({
      ...client,
      followUps: client.followUps.map((item) => (item.id === itemId ? { ...item, ...updates } : item)),
    });
  }

  function updatePaymentSummary(updates: Partial<KtdClientRecord["paymentSummary"]>) {
    if (!client) return;
    persist({ ...client, paymentSummary: { ...client.paymentSummary, ...updates } });
  }

  return (
    <main className="min-h-screen bg-[#fff9ef] text-[#16120a]">
      <FormPortalHeader
        brandHref="/admin/truevance-business-center"
        brandName="Business Center OS"
        brandSubtitle="Shared client operations"
        brandLogoAlt="Business Center OS"
        adminDirectoryHref="/admin/truevance-business-center"
        adminDirectoryLabel="Business Center Home"
        showPublicBackLink={false}
      />
      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,106,43,.18),transparent_34%),linear-gradient(120deg,rgba(5,5,5,.96),rgba(22,18,10,.9))]" />
        <div className="relative mx-auto grid max-w-7xl gap-5">
          <button
            type="button"
            onClick={() => navigate("/admin/forms-client-directory")}
            className="focus-ring inline-flex w-fit items-center gap-2 rounded-lg text-sm font-bold text-[#FF6A2B]"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Client Directory
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <HealthBadge health={health} />
            <span className="rounded-full border border-[#FF6A2B]/30 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#FF6A2B]">
              {client.projectStatus}
            </span>
          </div>
          <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">{client.clientName}</h1>
          <p className="max-w-3xl text-base font-semibold leading-8 text-[#fff9ef]/76">
            {client.eventName || "No project name set"} • {formatDisplayDate(client.eventDate)} • Last updated{" "}
            {formatDateTime(client.updatedAt)}
          </p>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          <DashboardBand
            items={[
              { label: "Total Project Value", value: currency(client.paymentSummary.totalProjectValue), icon: DollarSign },
              { label: "Current Balance Due", value: currency(balance), icon: CreditCard },
              { label: "Open Follow-Ups", value: client.followUps.filter((item) => item.status === "Pending").length, icon: ClipboardList },
              { label: "Checklist Completed", value: `${client.checklist.filter((item) => item.status === "Completed").length}/${client.checklist.length}`, icon: CheckCircle2 },
            ]}
          />

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionTitle icon={HeartPulse} title="Client Information" subtitle="Core record, service package, project status, and health override." />
            <form onSubmit={saveClientInfo} className="mt-5 grid gap-4 md:grid-cols-2">
              <TextInput label="Client Name" value={info.clientName} onChange={(value) => setInfo({ ...info, clientName: value })} />
              <TextInput label="Email" type="email" value={info.email} onChange={(value) => setInfo({ ...info, email: value })} />
              <TextInput label="Phone" type="tel" value={info.phone} onChange={(value) => setInfo({ ...info, phone: value })} />
              <TextInput
                label="Organization / Family / Business Name"
                value={info.organizationName}
                onChange={(value) => setInfo({ ...info, organizationName: value })}
              />
              <SelectInput label="Client Type" value={info.clientType} options={clientTypeOptions} onChange={(value) => setInfo({ ...info, clientType: value as ClientType })} />
              <TextInput label="Event / Project Name" value={info.eventName} onChange={(value) => setInfo({ ...info, eventName: value })} />
              <TextInput label="Event Date" type="date" value={info.eventDate} onChange={(value) => setInfo({ ...info, eventDate: value })} />
              <TextInput label="Selected Package" value={info.selectedPackage} onChange={(value) => setInfo({ ...info, selectedPackage: value })} />
              <SelectInput label="Project Status" value={info.projectStatus} options={clientStatusOptions} onChange={(value) => setInfo({ ...info, projectStatus: value as ClientStatus })} />
              <SelectInput
                label="Client Health Indicator"
                value={info.healthOverride || "Auto"}
                options={["Auto", ...healthOptions]}
                onChange={(value) => setInfo({ ...info, healthOverride: value === "Auto" ? "" : (value as ClientHealth) })}
              />
              <TextAreaInput className="md:col-span-2" label="Internal Notes" value={info.notes} onChange={(value) => setInfo({ ...info, notes: value })} />
              <div className="md:col-span-2">
                <p className="text-sm font-bold text-[#332a1f]">Selected Services</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {serviceOptions.map((service) => (
                    <label key={service} className="flex items-center gap-2 rounded-lg border border-[#D94A16]/16 bg-[#fffdf8] p-3 text-sm font-bold text-[#332a1f]">
                      <input
                        type="checkbox"
                        checked={info.selectedServices.includes(service)}
                        onChange={() => {
                          setInfo({
                            ...info,
                            selectedServices: info.selectedServices.includes(service)
                              ? info.selectedServices.filter((item) => item !== service)
                              : [...info.selectedServices, service],
                          });
                        }}
                        className="size-4 accent-[#D94A16]"
                      />
                      {service}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end md:col-span-2">
                <button type="submit" className="focus-ring min-h-11 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B]">
                  Save Client Information
                </button>
              </div>
            </form>
          </section>

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionTitle icon={Link2} title="Quick Links" subtitle="Store proposal, agreement, invoice, vault, forms, archive, delivery, and custom links." />
            <form onSubmit={addQuickLink} className="mt-5 grid gap-4 md:grid-cols-[220px_1fr]">
              <SelectInput label="Link Label" value={quickLink.label} options={quickLinkLabels} onChange={(value) => setQuickLink({ ...quickLink, label: value })} />
              <TextInput label="URL" type="url" value={quickLink.url} onChange={(value) => setQuickLink({ ...quickLink, url: value })} />
              <TextAreaInput className="md:col-span-2" label="Notes" value={quickLink.notes} onChange={(value) => setQuickLink({ ...quickLink, notes: value })} />
              <div className="flex justify-end md:col-span-2">
                <button type="submit" className="focus-ring min-h-11 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B]">
                  Add Quick Link
                </button>
              </div>
            </form>
            <RecordGrid>
              {client.quickLinks.map((link) => (
                <article key={link.id} className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                  <h3 className="font-display text-2xl font-bold">{link.label}</h3>
                  <p className="mt-2 text-sm font-semibold text-[#5c5246]">{link.notes || "No notes added."}</p>
                  <RecordActions onDelete={() => persist({ ...client, quickLinks: client.quickLinks.filter((item) => item.id !== link.id) })} url={link.url} />
                </article>
              ))}
            </RecordGrid>
          </section>

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionTitle icon={NotebookPen} title="Notes Timeline" subtitle="Newest notes appear first. Add, edit, or delete client communication and internal notes." />
            <form onSubmit={addOrUpdateNote} className="mt-5 grid gap-4 md:grid-cols-4">
              <TextInput label="Date" type="date" value={note.date} onChange={(value) => setNote({ ...note, date: value })} />
              <TextInput label="Time" type="time" value={note.time} onChange={(value) => setNote({ ...note, time: value })} />
              <SelectInput label="Note Category" value={note.category} options={noteCategoryOptions} onChange={(value) => setNote({ ...note, category: value as NoteCategory })} />
              <TextInput label="Follow-Up Date" type="date" value={note.followUpDate} onChange={(value) => setNote({ ...note, followUpDate: value })} />
              <TextAreaInput className="md:col-span-4" label="Note Text" value={note.text} onChange={(value) => setNote({ ...note, text: value })} />
              <div className="flex justify-end gap-2 md:col-span-4">
                {editingNoteId ? (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingNoteId("");
                      setNote({ date: new Date().toISOString().slice(0, 10), time: new Date().toTimeString().slice(0, 5), category: "Internal Note", text: "", followUpDate: "" });
                    }}
                    className="focus-ring min-h-11 rounded-lg border border-[#D94A16]/24 px-5 text-sm font-extrabold text-[#332a1f]"
                  >
                    Cancel Edit
                  </button>
                ) : null}
                <button type="submit" className="focus-ring min-h-11 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B]">
                  {editingNoteId ? "Save Note" : "Add Note"}
                </button>
              </div>
            </form>
            <div className="mt-5 grid gap-3">
              {sortNotesNewestFirst(client.timelineNotes).map((timelineNote) => (
                <article key={timelineNote.id} className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{timelineNote.category}</p>
                      <p className="mt-1 text-sm font-bold text-[#5c5246]">
                        {formatDisplayDate(timelineNote.date)} at {timelineNote.time || "No time"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => editNote(timelineNote)} className="focus-ring rounded-lg border border-[#D94A16]/24 px-3 py-2 text-xs font-black text-[#332a1f]">
                        Edit
                      </button>
                      <button type="button" onClick={() => deleteNote(timelineNote.id)} className="focus-ring rounded-lg border border-red-200 px-3 py-2 text-xs font-black text-red-800">
                        Delete
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 text-sm font-semibold leading-7 text-[#332a1f]">{timelineNote.text}</p>
                  {timelineNote.followUpDate ? <p className="mt-2 text-xs font-black uppercase tracking-[0.12em] text-[#D94A16]">Follow up: {formatDisplayDate(timelineNote.followUpDate)}</p> : null}
                </article>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionTitle icon={FileText} title="Forms & Documents" subtitle="Link-based document storage for proposals, agreements, forms, invoices, files, and delivery notes." />
            <form onSubmit={addDocument} className="mt-5 grid gap-4 md:grid-cols-[260px_1fr]">
              <SelectInput label="Document Type" value={documentForm.type} options={documentTypeOptions} onChange={(value) => setDocumentForm({ ...documentForm, type: value as DocumentType })} />
              <TextInput label="Document Label" value={documentForm.label} onChange={(value) => setDocumentForm({ ...documentForm, label: value })} />
              <TextInput className="md:col-span-2" label="External Link" type="url" value={documentForm.url} onChange={(value) => setDocumentForm({ ...documentForm, url: value })} />
              <TextAreaInput className="md:col-span-2" label="Notes" value={documentForm.notes} onChange={(value) => setDocumentForm({ ...documentForm, notes: value })} />
              <div className="flex justify-end md:col-span-2">
                <button type="submit" className="focus-ring min-h-11 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B]">
                  Add Document Link
                </button>
              </div>
            </form>
            <RecordGrid>
              {client.documents.map((documentRecord) => (
                <article key={documentRecord.id} className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{documentRecord.type}</p>
                  <h3 className="mt-1 font-display text-2xl font-bold">{documentRecord.label || documentRecord.type}</h3>
                  <p className="mt-2 text-sm font-semibold text-[#5c5246]">{documentRecord.notes || "No notes added."}</p>
                  <RecordActions
                    onDelete={() => persist({ ...client, documents: client.documents.filter((item) => item.id !== documentRecord.id) })}
                    url={documentRecord.url}
                  />
                </article>
              ))}
            </RecordGrid>
          </section>

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionTitle icon={DollarSign} title="Payment Summary" subtitle="Track project value, deposit, payments received, balance, status, and notes." />
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <TextInput label="Total Project Value" type="number" value={client.paymentSummary.totalProjectValue} onChange={(value) => updatePaymentSummary({ totalProjectValue: value })} />
              <TextInput label="Deposit Amount" type="number" value={client.paymentSummary.depositAmount} onChange={(value) => updatePaymentSummary({ depositAmount: value })} />
              <TextInput label="Payments Received" type="number" value={client.paymentSummary.paymentsReceived} onChange={(value) => updatePaymentSummary({ paymentsReceived: value })} />
              <SelectInput label="Payment Status" value={client.paymentSummary.paymentStatus} options={paymentStatusOptions} onChange={(value) => updatePaymentSummary({ paymentStatus: value as PaymentStatus })} />
              <TextInput label="Last Payment Date" type="date" value={client.paymentSummary.lastPaymentDate} onChange={(value) => updatePaymentSummary({ lastPaymentDate: value })} />
              <div className="rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
                <p className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">Current Balance Due</p>
                <p className="mt-2 font-display text-3xl font-bold text-[#050505]">{currency(balance)}</p>
              </div>
              <TextAreaInput className="md:col-span-3" label="Payment Notes" value={client.paymentSummary.paymentNotes} onChange={(value) => updatePaymentSummary({ paymentNotes: value })} />
            </div>
          </section>

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionTitle icon={ClipboardList} title="Project Progress Checklist" subtitle="Update each project milestone manually as the client journey moves forward." />
            <div className="mt-5 grid gap-3">
              {client.checklist.map((item) => (
                <article key={item.id} className="grid gap-3 rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4 lg:grid-cols-[1fr_180px_170px_1.1fr] lg:items-center">
                  <div>
                    <p className="font-bold text-[#050505]">{item.label}</p>
                    <p className="mt-1 text-xs font-black uppercase tracking-[0.12em] text-[#D94A16]">{item.status}</p>
                  </div>
                  <SelectInput hideLabel label={`${item.label} status`} value={item.status} options={checklistStatusOptions} onChange={(value) => updateChecklistItem(item.id, { status: value as ChecklistStatus })} />
                  <TextInput hideLabel label={`${item.label} completion date`} type="date" value={item.completionDate} onChange={(value) => updateChecklistItem(item.id, { completionDate: value })} />
                  <TextInput hideLabel label={`${item.label} notes`} value={item.notes} onChange={(value) => updateChecklistItem(item.id, { notes: value })} placeholder="Notes" />
                </article>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6">
            <SectionTitle icon={AlertTriangle} title="Follow-Ups" subtitle="Track upcoming and overdue follow-ups by priority, status, reason, and notes." />
            <form onSubmit={addFollowUp} className="mt-5 grid gap-4 md:grid-cols-4">
              <TextInput label="Follow-Up Date" type="date" value={followUpForm.followUpDate} onChange={(value) => setFollowUpForm({ ...followUpForm, followUpDate: value })} />
              <SelectInput label="Priority" value={followUpForm.priority} options={followUpPriorityOptions} onChange={(value) => setFollowUpForm({ ...followUpForm, priority: value as FollowUpPriority })} />
              <SelectInput label="Status" value={followUpForm.status} options={followUpStatusOptions} onChange={(value) => setFollowUpForm({ ...followUpForm, status: value as FollowUpStatus })} />
              <TextInput label="Reason" value={followUpForm.reason} onChange={(value) => setFollowUpForm({ ...followUpForm, reason: value })} />
              <TextAreaInput className="md:col-span-4" label="Notes" value={followUpForm.notes} onChange={(value) => setFollowUpForm({ ...followUpForm, notes: value })} />
              <div className="flex justify-end md:col-span-4">
                <button type="submit" className="focus-ring min-h-11 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B]">
                  Add Follow-Up
                </button>
              </div>
            </form>
            <div className="mt-5 grid gap-3">
              {sortFollowUps(client.followUps).map((followUp) => {
                const overdue = followUp.status === "Pending" && isDateOverdue(followUp.followUpDate);
                return (
                  <article key={followUp.id} className={`rounded-lg border p-4 ${overdue ? "border-red-300 bg-red-50" : "border-[#D94A16]/18 bg-[#fffdf8]"}`}>
                    <div className="grid gap-3 lg:grid-cols-[1fr_160px_160px_auto] lg:items-center">
                      <div>
                        <p className="font-display text-2xl font-bold text-[#050505]">{followUp.reason}</p>
                        <p className="mt-1 text-sm font-semibold text-[#5c5246]">
                          {formatDisplayDate(followUp.followUpDate)} {overdue ? "• Overdue" : ""}
                        </p>
                        <p className="mt-2 text-sm font-semibold leading-6 text-[#332a1f]">{followUp.notes}</p>
                      </div>
                      <SelectInput hideLabel label="Priority" value={followUp.priority} options={followUpPriorityOptions} onChange={(value) => updateFollowUp(followUp.id, { priority: value as FollowUpPriority })} />
                      <SelectInput hideLabel label="Status" value={followUp.status} options={followUpStatusOptions} onChange={(value) => updateFollowUp(followUp.id, { status: value as FollowUpStatus })} />
                      <button
                        type="button"
                        onClick={() => persist({ ...client, followUps: client.followUps.filter((item) => item.id !== followUp.id) })}
                        className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-red-200 px-4 text-sm font-extrabold text-red-800"
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                        Delete
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

function DashboardBand({ items }: { items: { label: string; value: string | number; icon: typeof DollarSign }[] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <article key={item.label} className="rounded-lg border border-[#D94A16]/22 bg-white p-4 shadow-[0_18px_45px_rgba(45,32,8,.08)]" title={item.label}>
            <Icon className="size-5 text-[#D94A16]" aria-hidden="true" />
            <p className="mt-4 font-display text-3xl font-bold text-[#050505]">{item.value}</p>
            <p className="mt-1 text-xs font-black uppercase tracking-[0.12em] text-[#5c5246]">{item.label}</p>
            <p className="mt-3 text-sm font-semibold leading-6 text-[#5c5246]">Client-level operational summary for the selected record.</p>
          </article>
        );
      })}
    </div>
  );
}

function RecordGrid({ children }: { children: ReactNode }) {
  return <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">{children}</div>;
}

function RecordActions({ onDelete, url }: { onDelete: () => void; url: string }) {
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="focus-ring inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-[#050505] px-4 text-sm font-extrabold text-[#FF6A2B]"
        title="Open the linked document or page"
      >
        Open Link
        <ExternalLink className="size-4" aria-hidden="true" />
      </a>
      <button
        type="button"
        onClick={onDelete}
        className="focus-ring inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-red-200 px-4 text-sm font-extrabold text-red-800"
        title="Delete this linked record"
      >
        Delete
      </button>
    </div>
  );
}

function SectionTitle({ icon: Icon, subtitle, title }: { icon: typeof Users; subtitle: string; title: string }) {
  return (
    <div className="flex flex-col gap-3 border-b border-[#D94A16]/16 pb-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Client Profile</p>
        <h2 className="mt-1 flex items-center gap-2 font-display text-3xl font-bold text-[#050505]">
          <Icon className="size-6 text-[#D94A16]" aria-hidden="true" />
          {title}
        </h2>
        <p className="mt-3 max-w-4xl text-sm font-semibold leading-7 text-[#5c5246]">{subtitle}</p>
      </div>
    </div>
  );
}

function HealthBadge({ health }: { health: ClientHealth }) {
  const classes =
    health === "On Track"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : health === "Needs Attention"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : "border-red-200 bg-red-50 text-red-800";

  return <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-black uppercase tracking-[0.12em] ${classes}`}>{health}</span>;
}

function TextInput({
  className = "",
  hideLabel = false,
  label,
  onChange,
  placeholder,
  required,
  type = "text",
  value,
}: {
  className?: string;
  hideLabel?: boolean;
  label: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  type?: string;
  value: string;
}) {
  return (
    <label className={`grid gap-2 text-sm font-bold text-[#332a1f] ${className}`}>
      <span className={hideLabel ? "sr-only" : ""}>{label}</span>
      <input
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="form-field focus-ring min-h-11 rounded-lg px-4 text-sm font-semibold"
      />
    </label>
  );
}

function TextAreaInput({
  className = "",
  label,
  onChange,
  value,
}: {
  className?: string;
  label: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <label className={`grid gap-2 text-sm font-bold text-[#332a1f] ${className}`}>
      {label}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="form-field focus-ring min-h-28 rounded-lg px-4 py-3 text-sm font-semibold"
      />
    </label>
  );
}

function SelectInput({
  hideLabel = false,
  label,
  onChange,
  options,
  value,
}: {
  hideLabel?: boolean;
  label: string;
  onChange: (value: string) => void;
  options: readonly string[];
  value: string;
}) {
  return (
    <label className="grid gap-2 text-sm font-bold text-[#332a1f]">
      <span className={hideLabel ? "sr-only" : ""}>{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="form-field focus-ring min-h-11 rounded-lg px-4 text-sm font-bold"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}
